import { Box, Button, Loader, Search, SectionHelper, SidePanel } from "@wix/design-system";
import lodash from "lodash";
import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  EditorContext,
  ModalServiceContext,
  PluginsEventsContext,
  RicosContext
} from "./../../ricos-context";
import { AppEmbedData_AppType } from "./../../ricos-schema/legacy/wix/rich_content/v1";
import { useBiNext } from "./../../_shared/bi-context-provider/bi-context-provider";
import { MEDIA_POPOVERS_BUTTONS_NAMES_BI, uuid } from "./../../wix-rich-content-common";
import {
  FooterActions,
  Panel,
  PanelHeader,
  usePanelContainerElement
} from "./../../ui-components";
import { unreachable } from "../../ricos-content/ts-utils";
import { contentTypeMap } from "../constants";
import {
  INSERT_VERTICAL_EMBED_CANCEL_BUTTON,
  INSERT_VERTICAL_EMBED_FOOTER_ACTIONS,
  INSERT_VERTICAL_EMBED_ITEMS_LIST,
  INSERT_VERTICAL_EMBED_MODAL,
  INSERT_VERTICAL_EMBED_SEARCH_INPUT
} from "../data-hooks";
import { ItemsList } from "../toolbar/ItemsList";
import { VERTICAL_EMBED_TYPE } from "../types";
const InsertModal = ({
  componentData,
  verticalsApi,
  nodeId,
  modalId,
  _state
}) => {
  const { locale, t, isMobile } = useContext(RicosContext);
  const { getPublicCommands } = useContext(EditorContext);
  const modalService = useContext(ModalServiceContext) || {};
  const pluginsEvents = useContext(PluginsEventsContext);
  const logBi = useBiNext({ modalId, pluginId: "verticalEmbed", nodeId });
  const containerEl = usePanelContainerElement();
  const contentType = contentTypeMap[componentData.type];
  const [state, setState] = useState(
    _state ?? {
      status: "LOADING",
      searchText: "",
      requestId: uuid()
    }
  );
  useEffect(() => {
    if (_state) {
      return;
    }
    fetchEmbedItems("");
  }, []);
  const verticalApi = useMemo(() => {
    if (verticalsApi && isTypeAppEmbed(componentData.type)) {
      return verticalsApi(getLowerCaseAppEmbedType(componentData.type), locale);
    }
  }, [verticalsApi, componentData.type, locale]);
  const fetchEmbedItems = lodash.debounce((inputString = "") => {
    const requestId = uuid();
    setState(() => ({
      status: "LOADING",
      searchText: inputString,
      requestId
    }));
    (async () => {
      try {
        const items = await verticalApi?.search(inputString);
        setState((prev) => {
          if (prev.status === "LOADING" && prev.requestId !== requestId) {
            return prev;
          }
          return {
            status: "READY",
            items: items ?? [],
            selectedItem: void 0,
            searchText: inputString,
            showSelectionError: false
          };
        });
      } catch (e) {
        console.error("Failed to load products", e);
        setState((prev) => {
          if (prev.status === "LOADING" && prev.requestId !== requestId) {
            return prev;
          }
          return {
            status: "READY",
            items: [],
            selectedItem: void 0,
            searchText: inputString,
            showSelectionError: false
          };
        });
      }
    })();
  }, 300);
  const closeModal = () => modalService.closeModal(modalId);
  const onSave = () => {
    pluginsEvents.publishPluginPopoverClick?.({
      pluginId: VERTICAL_EMBED_TYPE,
      buttonName: MEDIA_POPOVERS_BUTTONS_NAMES_BI.embed
    });
    if (state.status !== "READY" || !state.selectedItem) {
      setState((prev) => ({ ...prev, showSelectionError: true }));
      return;
    }
    const embedData = toAppEmbedData(state.selectedItem, componentData);
    insertEmbedNode(embedData);
    closeModal();
  };
  const insertEmbedNode = (verticalEmbed) => {
    const publicCommands = getPublicCommands();
    setTimeout(
      () => nodeId ? publicCommands?.setBlock(nodeId, VERTICAL_EMBED_TYPE, verticalEmbed) : publicCommands?.insertBlockWithBlankLines(VERTICAL_EMBED_TYPE, verticalEmbed),
      50
    );
    closeModal();
  };
  if (!verticalApi && !componentData) {
    return null;
  }
  return /* @__PURE__ */ React.createElement(
    Panel,
    {
      height: isMobile ? "100vh" : "540px",
      onCloseButtonClick: closeModal,
      dataHook: INSERT_VERTICAL_EMBED_MODAL
    },
    /* @__PURE__ */ React.createElement(PanelHeader, { title: t(`Embed_Vertical_${contentType}_Title`) }),
    /* @__PURE__ */ React.createElement(Box, { padding: "SP3" }, /* @__PURE__ */ React.createElement(
      Search,
      {
        size: "small",
        value: state.searchText,
        onChange: (e) => {
          fetchEmbedItems(e.target.value);
          logBi({
            dataHook: INSERT_VERTICAL_EMBED_SEARCH_INPUT,
            value: e.target.value
          });
        },
        onClear: () => fetchEmbedItems(""),
        placeholder: t(`Embed_Vertical_${contentType}_Placeholder`),
        dataHook: INSERT_VERTICAL_EMBED_SEARCH_INPUT
      }
    )),
    (() => {
      switch (state.status) {
        case "LOADING":
          return /* @__PURE__ */ React.createElement(Box, { align: "center", verticalAlign: "middle", height: "252px" }, /* @__PURE__ */ React.createElement(Loader, null));
        case "READY":
          return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Box, { direction: "vertical", paddingBottom: "SP3", overflow: "auto" }, /* @__PURE__ */ React.createElement(
            ItemsList,
            {
              selectedItem: state.selectedItem,
              items: state.items,
              onClick: (item) => {
                logBi({
                  dataHook: INSERT_VERTICAL_EMBED_ITEMS_LIST,
                  value: contentType
                });
                setState((prev) => {
                  if (prev.status === "READY") {
                    return {
                      ...prev,
                      showSelectionError: false,
                      selectedItem: item.id === prev.selectedItem?.id ? void 0 : item
                    };
                  }
                  return prev;
                });
              },
              contentType,
              modalRef: containerEl
            }
          )), state.showSelectionError && /* @__PURE__ */ React.createElement(Box, { padding: "SP3", position: "absolute", bottom: "40px" }, /* @__PURE__ */ React.createElement(
            SectionHelper,
            {
              appearance: "danger",
              title: t("Embed_Vertical_Nothing_Selected_Error_Title"),
              showCloseButton: true,
              onClose: () => setState((prev) => ({ ...prev, showSelectionError: false }))
            },
            t("Embed_Vertical_Nothing_Selected_Error_Description")
          )));
        default:
          unreachable(state);
          return null;
      }
    })(),
    /* @__PURE__ */ React.createElement(SidePanel.Footer, { showDivider: true }, state.status === "READY" && state.items.length === 0 && state.searchText.length === 0 ? /* @__PURE__ */ React.createElement(Box, { align: "right", gap: "SP2" }, /* @__PURE__ */ React.createElement(
      Button,
      {
        onClick: () => {
          logBi({
            dataHook: INSERT_VERTICAL_EMBED_CANCEL_BUTTON
          });
          closeModal();
        },
        priority: "primary",
        dataHook: INSERT_VERTICAL_EMBED_CANCEL_BUTTON,
        size: "small"
      },
      t("Embed_Vertical_Close_Button")
    )) : /* @__PURE__ */ React.createElement(
      FooterActions,
      {
        dataHook: INSERT_VERTICAL_EMBED_FOOTER_ACTIONS,
        onCancel: closeModal,
        logBi,
        onSave,
        saveButtonLabel: t("Embed_Vertical_Add_Button")
      }
    ))
  );
};
const isTypeAppEmbed = (type) => Object.values(AppEmbedData_AppType).includes(type);
const getLowerCaseAppEmbedType = (type) => type.toLowerCase();
const toAppEmbedData = (item, componentData) => {
  const verticalData = componentData.type === "BOOKING" ? { bookingData: { durations: item.durations + "" } } : componentData.type === "EVENT" ? { eventData: { scheduling: item.scheduling, location: item.location } } : {};
  return {
    ...componentData,
    name: item.name,
    itemId: item.id,
    url: item.pageUrl,
    image: { src: { url: item.imageSrc }, height: item.imageHeight, width: item.imageWidth },
    ...verticalData
  };
};
export {
  InsertModal
};
//# sourceMappingURL=InsertModal.js.map
